import {
    FOOTER_ROLE,
    HEADER_ROLE,
    OBJ,
    PAGE_BORDER_ROLE,
    WATERMARK_ROLE,
} from '../constants';
import { makeImage, makeShape, makeText } from './objects';
import { makeTable } from './tableLayout';
import { recomputeTable } from './drawObjects';

/**
 * The switches that put the same thing on every page — watermark, header,
 * footer, page colour, page border — and take it away again.
 *
 * They follow the page-number switch: everything they add carries a role, so a
 * second press removes exactly what the first one added. Without that, turning
 * a watermark off on a forty-page file means forty deletions by hand.
 */

/** Is this switch currently on anywhere in the document? */
export function hasRole(objectsByUid, role) {
    return Object.values(objectsByUid).some((list) => list?.some((o) => o.role === role));
}

/**
 * Put `build(page, index)` on every page under `role`, replacing whatever the
 * same switch left there last time. `build` returning null leaves that page be.
 */
export function applyToEveryPage(state, role, build, { atBack = false } = {}) {
    const next = {};
    state.pages.forEach((page, index) => {
        const kept = (state.objectsByUid[page.uid] || []).filter((o) => o.role !== role);
        const made = build(page, index);
        if (!made) { next[page.uid] = kept; return; }
        const stamped = { ...made, role };
        // A page colour is the page, not something on it, so it goes underneath
        // everything — including whatever was already drawn on the page.
        next[page.uid] = atBack ? [stamped, ...kept] : [...kept, stamped];
    });
    return { objectsByUid: { ...state.objectsByUid, ...next } };
}

export function clearRole(state, role) {
    const next = {};
    state.pages.forEach((page) => {
        next[page.uid] = (state.objectsByUid[page.uid] || []).filter((o) => o.role !== role);
    });
    return { objectsByUid: { ...state.objectsByUid, ...next } };
}

/* ------------------------------------------------------------- watermark */

/** Big, pale, and turned across the page — the way a watermark is always set. */
export function buildWatermark(page, text, settings) {
    const fontSize = Math.max(28, Math.round(Math.min(page.width, page.height) / 7));
    const width = Math.min(page.width * 0.92, fontSize * Math.max(4, text.length) * 0.62);
    const height = fontSize * 1.3;
    return {
        ...makeText(
            {
                x: (page.width - width) / 2,
                y: (page.height - height) / 2,
                w: width,
                h: height,
            },
            {
                ...settings,
                fontSize,
                bold: true,
                align: 'center',
                color: '#9AA0A6',
            },
            text,
        ),
        rotation: -32,
        opacity: 0.28,
    };
}

/**
 * A logo instead of a word.
 *
 * Same role, same rotation, same paleness as the text one — a watermark is a
 * watermark whatever it is made of, and carrying the role means the switch
 * still takes it off again and an edit on one page still reaches all of them.
 *
 * Sized to the SHORT edge so a wide banner and a tall crest both land inside
 * the page, and drawn straight rather than turned: a logo on the diagonal reads
 * as a mistake, where a word on the diagonal reads as a watermark.
 */
export function buildImageWatermark(page, src, naturalW, naturalH) {
    const ratio = naturalW > 0 && naturalH > 0 ? naturalW / naturalH : 1;
    const limit = Math.min(page.width, page.height) * 0.62;
    const width = ratio >= 1 ? Math.min(page.width * 0.72, limit * ratio) : limit * ratio;
    const height = width / ratio;
    return {
        ...makeImage(
            {
                x: (page.width - width) / 2,
                y: (page.height - height) / 2,
                w: width,
                h: height,
            },
            src,
            naturalW,
            naturalH,
        ),
        opacity: 0.18,
    };
}

/* --------------------------------------------------------- header / footer */

const MARGIN = 28;

/**
 * The running line, inside the page margins — which is where Word puts it and
 * what the margin guides on screen promise it will do.
 */
export function buildRunningText(page, text, settings, where, margins = null) {
    const fontSize = 10;
    const height = fontSize * 1.6;
    const left = margins?.left ?? MARGIN;
    const right = margins?.right ?? MARGIN;
    const top = (margins?.top ?? MARGIN) / 2;
    const bottom = (margins?.bottom ?? MARGIN) / 2;
    return makeText(
        {
            x: left,
            y: where === 'header' ? Math.max(6, top - height) : page.height - bottom,
            w: Math.max(40, page.width - left - right),
            h: height,
        },
        { ...settings, fontSize, align: 'center' },
        text,
    );
}

/* ------------------------------------------------------------ page border */

export function buildPageBorder(page, stroke, strokeWidth = 2) {
    // Inset by its own width so the line lands on the paper rather than half
    // off the edge, where a printer would clip it.
    const inset = MARGIN / 2 + strokeWidth;
    return makeShape(OBJ.RECT, {
        x: inset,
        y: inset,
        w: page.width - inset * 2,
        h: page.height - inset * 2,
    }, { fill: 'transparent', stroke, strokeWidth });
}

/* -------------------------------------------------------------- the table */

/**
 * A real table object (one unit): movable, resizable, editable cell-by-cell, and
 * flow-aware in document mode. Placed centred, a little below the top of the page by
 * default — the caller (document mode) may reposition it into the text flow.
 */
export function buildTable(page, rows, cols, settings = {}) {
    const width = Math.min(page.width - MARGIN * 2, 460);
    const x = (page.width - width) / 2;
    const y = Math.max(MARGIN, page.height * 0.3);
    const table = makeTable({ x, y, w: width }, rows, cols, {
        stroke: settings.stroke || '#1C1815',
        strokeWidth: 1,
    });
    return [recomputeTable(table)];
}
